import '@fontsource/roboto';
import { responsiveFontSizes } from '@mui/material';
import { createTheme, ThemeProvider, useTheme as useTheme$1 } from '@mui/material/styles';
import * as colors from '@ux-foundry/palette/colors';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createContext, useContext, useState, useEffect } from 'react';
import { darkThemePalette, lightThemePalette } from '@ux-foundry/palette';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const typography = {
    fontSize: 16,
    fontFamily: ['Roboto', 'Arial', 'sans-serif'].join(','),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    appTitle: {
        // Application title : 22px/Light
        fontSize: '1.375rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    h1: {
        fontSize: '2.5rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    h2: {
        // Page Title : 30px/Bold
        fontSize: '1.875rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    h3: {
        // Section Title : 18px/Light
        fontSize: '1.375rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    h4: {
        // Information Group Header : 18px/Bold
        fontSize: '1.125rem',
        fontWeight: '300',
        ineHeight: '1.25',
    },
    h5: {
        // Sub-Heading : 16px/Bold
        fontSize: '1rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    h6: {
        // Caption : 12x/Bold
        fontSize: '.75rem',
        fontWeight: '300',
        lineHeight: '1.25',
    },
    subtitle1: {
        // Labels & Values : 14px/Regular
        fontSize: '.875rem',
        fontWeight: '300',
        lineHeight: '1.5',
    },
    subtitle2: {
        // Error Message : 14px/Bold
        fontSize: '.875rem',
        fontWeight: '300',
        lineHeight: '1.5',
    },
    body1: {
        // Body Text : 16px/Regular
        fontSize: '1rem',
        fontWeight: '400',
        lineHeight: '1.625',
    },
    body2: {
        // Legal Text : 12px/Regular
        fontSize: '.75rem',
        fontWeight: '400',
        lineHeight: '1.625',
    },
    button: {
        fontSize: '.875rem',
        fontWeight: '400',
        lineHeight: '1.75',
        textTransform: 'none',
    },
    caption: {
        fontSize: '.65rem',
        fontWeight: '400',
        lineHeight: '1.625',
    },
};

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const darkTheme = createTheme({
    palette: {
        common: {
            black: colors.common.black,
            white: colors.common.white,
        },
        mode: 'dark',
        primary: {
            light: colors.blue[100],
            main: colors.blue[300],
            dark: colors.blue[500],
        },
        secondary: {
            light: colors.blue[100],
            main: colors.blue[300],
            dark: colors.blue[500],
        },
        success: {
            light: colors.green[100],
            main: colors.green[300],
            dark: colors.green[500],
        },
        info: {
            light: colors.blue[100],
            main: colors.blue[300],
            dark: colors.blue[500],
        },
        warning: {
            light: colors.orange[100],
            main: colors.orange[300],
            dark: colors.orange[500],
        },
        error: {
            light: colors.red[100],
            main: colors.red[300],
            dark: colors.red[500],
        },
        background: {
            default: colors.gray.A900,
            paper: colors.gray.A900,
        },
        grey: colors.gray,
    },
    typography: {
        ...typography,
    },
});
/* Allow all components to use responsive font sizes */
const fsnDarkTheme = responsiveFontSizes(darkTheme);

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const lightTheme = createTheme({
    palette: {
        common: {
            black: colors.common.black,
            white: colors.common.white,
        },
        mode: 'light',
        primary: {
            light: colors.blue[300],
            main: colors.blue[700],
            dark: colors.blue[900],
        },
        secondary: {
            light: colors.gray[200],
            main: colors.gray[700],
            dark: colors.gray[900],
        },
        success: {
            light: colors.green[300],
            main: colors.green[700],
            dark: colors.green[900],
        },
        info: {
            light: colors.blue[300],
            main: colors.blue[700],
            dark: colors.blue[900],
        },
        warning: {
            light: colors.orange[200],
            main: colors.orange[700],
            dark: colors.orange[900],
        },
        error: {
            light: colors.red[200],
            main: colors.red[700],
            dark: colors.red[900],
        },
        background: {
            default: colors.common.offWhite,
            paper: colors.common.white,
        },
        grey: colors.gray,
    },
    typography: {
        ...typography,
    },
});
/* Allow all components to use responsive font sizes */
const fsnLightTheme = responsiveFontSizes(lightTheme);

/*
 * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Context object with default context.
 */
const FsnThemeContext = createContext(['light', () => { }]);
/**
 * Custom hook to abstract out the call to `useContext(FsnThemeContext)`
 */
const useThemeSetting = () => {
    return useContext(FsnThemeContext);
};

function useTheme(themeSetting) {
    const isDarkModeEnabled = useMediaQuery('(prefers-color-scheme: dark)');
    // If theme setting is system, then derive the theme from media query hook, otherwise, use appropriate theme.
    switch (themeSetting) {
        case 'system':
            return isDarkModeEnabled ? fsnDarkTheme : fsnLightTheme;
        case 'dark':
            return fsnDarkTheme;
        default:
            return fsnLightTheme;
    }
}
const createGlobalStyles = (theme) => {
    return {
        body: {
            overflow: 'hidden',
        },
        scrollBar: {
            '*::-webkit-scrollbar': {
                width: '17px',
                height: '17px',
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'inherit',
            },
            '*::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[500],
                borderWidth: 4,
                borderStyle: 'solid',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'transparent',
                borderRadius: 10,
                backgroundClip: 'padding-box',
            },
        },
    };
};
/**
 * The Theme Provider component provides a wrapper around MUI's ThemeProvider, using the dark and light Fusion
 * Themes.  You can pass it `'dark'`, `'light'`, or `'system'` as the theme setting, and it will keep track of the currently applied theme.
 * If using `'system'`, the provider will derive the appropriate theme from the user's system setting.  Persisting the
 * user's ThemeSetting across sessions is left to the implementing app.
 */
const FsnThemeProvider = ({ themeSetting = 'light', children }) => {
    // State for holding the current setting
    const [currentThemeSetting, setThemeSetting] = useState(themeSetting);
    // Get the theme object from the setting
    const theme = useTheme(currentThemeSetting);
    const globalStyles = createGlobalStyles(theme);
    const inputGlobalStyles = jsx(GlobalStyles, { styles: globalStyles.scrollBar });
    /** Side Effect for updating state when incoming themeSetting prop changes */
    useEffect(() => {
        if (themeSetting !== currentThemeSetting) {
            setThemeSetting(themeSetting);
        }
    }, [themeSetting]);
    return (jsx(FsnThemeContext.Provider, { value: [currentThemeSetting, setThemeSetting], children: jsxs(ThemeProvider, { theme: theme, children: [jsx(CssBaseline, {}), inputGlobalStyles, children] }) }));
};

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Uses the useTheme hook to determine if dark or light theme is active, and return the appropriate palette object
 */
const usePalette = () => {
    const currentTheme = useTheme$1();
    return currentTheme.palette.mode === 'dark' ? darkThemePalette : lightThemePalette;
};

export { FsnThemeProvider, fsnDarkTheme, fsnLightTheme, usePalette, useThemeSetting };
